<template>
  <div
    class="bg-theme-card rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.01] cursor-pointer flex flex-col h-full relative group border border-theme-border"
  >
    <!-- Badges -->
    <div
      v-if="item.is_featured || item.is_trending"
      class="absolute top-2 left-2 z-10 flex flex-col gap-1"
    >
      <span
        v-if="item.is_trending"
        class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-primary-600 text-white shadow"
      >
        <i class="pi pi-bolt text-[9px]"></i> Trending
      </span>
      <span
        v-if="item.is_featured"
        class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-neutral-900 text-secondary-500 border border-secondary-500 shadow"
      >
        <i class="pi pi-star-fill text-[9px]"></i> Featured
      </span>
    </div>

    <!-- Image -->
    <div class="relative w-full aspect-[4/3] overflow-hidden bg-theme-bg2">
      <img
        v-if="item.images?.length > 0"
        :src="getFullImageUrl(item.images[0].image_url)"
        :alt="`Image of ${item.name}`"
        class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        loading="lazy"
        @error="handleImageError"
      />
      <div v-else class="absolute inset-0 flex items-center justify-center">
        <i class="pi pi-image text-4xl text-theme-muted"></i>
      </div>

      <!-- Availability pill -->
      <span
        v-if="!item.is_available || item.available_quantity === 0"
        class="absolute bottom-2 left-2 px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-500/90 text-white"
      >
        Unavailable
      </span>
    </div>

    <!-- Content -->
    <div class="px-2 pt-1.5 pb-2 sm:px-3 sm:pt-2.5 sm:pb-3 flex flex-col flex-grow gap-1 sm:gap-1.5">
      <!-- Owner -->
      <div class="flex items-center gap-1 sm:gap-1.5">
        <img
          v-if="item.owner?.image_url"
          :src="item.owner.image_url"
          :alt="ownerName"
          class="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover border border-theme-border"
        />
        <div
          v-else
          class="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-[10px] sm:text-xs font-bold text-primary-600"
        >
          {{ ownerInitial }}
        </div>
        <span class="text-xs sm:text-sm text-theme-text2 truncate flex-1">{{ ownerName }}</span>
        <i
          v-if="item.owner?.is_verified"
          class="pi pi-verified text-xs sm:text-sm text-primary-500"
        ></i>
      </div>

      <!-- Name -->
      <h3 class="text-xs sm:text-sm font-semibold text-theme-text line-clamp-1 leading-snug">
        {{ item.name }}
      </h3>

      <!-- Rating + Distance -->
      <div class="flex items-center justify-between text-[11px] sm:text-sm">
        <div class="flex items-center gap-0.5 sm:gap-1 min-w-0">
          <span class="text-yellow-500 hidden sm:inline">{{ renderStars(item.rating) }}</span>
          <span class="text-yellow-500 sm:hidden">★</span>
          <span class="text-theme-text2 truncate">{{ (item.rating || 0).toFixed(1) }} ({{ item.equipment_reviews?.length || 0 }})</span>
        </div>
        <span
          v-if="distance"
          class="inline-flex items-center gap-0.5 font-semibold text-theme-text text-[10px] sm:text-sm shrink-0 ml-1"
        >
          <i class="pi pi-map-marker text-[9px] sm:text-xs text-primary-500"></i>
          {{ distance }}
        </span>
      </div>

      <!-- Pricing -->
      <div class="mt-auto">
        <div class="flex items-center gap-1 mb-1">
          <button
            v-for="tier in rateTiers"
            :key="tier.key"
            @click.stop="activeRate = tier.key"
            class="px-1.5 py-0.5 sm:px-2 rounded-full text-[10px] sm:text-xs font-semibold transition-colors"
            :class="activeRate === tier.key
              ? 'bg-primary-500 text-white'
              : 'bg-theme-bg2 text-theme-text2 hover:bg-theme-bg2/80'"
          >
            {{ tier.label }}
          </button>
        </div>
        <p class="text-primary-600 font-extrabold text-sm sm:text-base leading-tight">
          {{ formatPrice(currentRate) }}
          <span class="text-theme-muted text-[10px] sm:text-xs font-normal">/ {{ ratePeriod }}</span>
        </p>
      </div>

      <!-- CTA -->
      <button
        @click.stop="$emit('click')"
        class="mt-0.5 sm:mt-1 w-full py-1 sm:py-1.5 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1"
        :class="item.is_featured
          ? 'bg-neutral-900 text-secondary-500 hover:bg-secondary-500 hover:text-neutral-900 focus:ring-secondary-500'
          : 'bg-primary-600 text-white hover:bg-primary-500 focus:ring-primary-600'"
      >
        {{ item.is_featured ? '✨ Lease Now' : 'View Details' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { resolveImageUrl } from '@/composables/useImageUrl';
import { useCurrency } from '@/composables/useCurrency';
import { useLocation } from '@/composables/useLocation';

const { formatPrice } = useCurrency();
const { getDistance } = useLocation();

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
});

const getFullImageUrl = resolveImageUrl;

const handleImageError = (e) => {
  e.target.src = '/placeholder-image.jpg';
  e.target.classList.add('object-cover');
};

// Owner helpers
const ownerName = computed(() => {
  const o = props.item.owner;
  if (!o) return 'Unknown';
  if (o.first_name || o.last_name) return `${o.first_name || ''} ${o.last_name || ''}`.trim();
  if (o.company_name) return o.company_name;
  return 'Unknown';
});

const ownerInitial = computed(() => ownerName.value.charAt(0).toUpperCase());

// Distance
const distance = computed(() => getDistance(props.item));

// Rate tiers
const activeRate = ref('day');
const rateTiers = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

const currentRate = computed(() => {
  if (activeRate.value === 'week') return props.item.weekly_rate ?? props.item.hourly_rate * 7 * 0.9;
  if (activeRate.value === 'month') return props.item.monthly_rate ?? props.item.hourly_rate * 30 * 0.8;
  return props.item.hourly_rate;
});

const ratePeriod = computed(() =>
  activeRate.value === 'week' ? 'Week' : activeRate.value === 'month' ? 'Month' : 'Day'
);

// Stars
const renderStars = (rating) => {
  const r = rating || 0;
  const full = Math.floor(r);
  const half = r % 1 >= 0.5 ? 1 : 0;
  return '★'.repeat(full) + (half ? '☆' : '') + '☆'.repeat(5 - full - half);
};

defineEmits(['click']);
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
